// @fileoverview : 영업관리 > 수주현황

import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { users } from "@/api/users/useUsersQuery";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { PurchaseOrderCreateForm } from "@/features/purchaseOrder/form/createForm";
import { PurchaseOrderDetailForm } from "@/features/purchaseOrder/form/detailForm";
import { PurchaseOrderItemsOutgoingForm } from "@/features/purchaseOrder/form/purchaseOrderItemsOutgoingForm";
import { PurchaseOrderUpdateForm } from "@/features/purchaseOrder/form/updateForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { UsersDetailForm } from "@/features/users/components/form/UsersDetailForm";
import DatetimeUtil from "@/utils/dateTimeUtil";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { purchaseOrderStatusColor, setPurchaseOrderStatus } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { setToLocaleString } from "@/utils/unitMark";
import { ActionIcon, Avatar, Badge, Button, Flex, Text, Tooltip } from "@mantine/core";
import {
  PartnersGet200ResponseRowsInnerTypeEnum,
  PurchaseOrdersGet200Response,
  PurchaseOrdersGet200ResponseRowsInner,
  ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconPlus, IconRecycle, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const DeliveryInformantion = () => {
  const { t } = useTranslation();
  const { data: userData } = useQuery(users.who({}));
  const userCode = userData?.data.code;
  const partnerType = userData?.data?.partner?.type;

  const { openModal, closeModal } = useModal();
  const { query, setQuery, selectedRows, refetch, resetQueryStrings } = useStandardLayout();
  // const [isBeta] = useLocalStorage<boolean>({
  //   key: "isBeta",
  //   defaultValue: false, // 기본값은 모든 컬럼이 체크됨
  // });
  useEffect(() => {
    if (query.length === 0) {
      // query가 비어 있을 때만 실행
      setQuery([
        { direction: { $eq: "INBOUND" } },
        {
          $or: [{ creatorUserCode: userCode }, { partnerCode: userCode }],
        },
      ]);
    }

    if (userData?.data?.externalUserType) {
      setQuery([{ partnerCode: { $eq: userData?.data?.partner?.code } }]);
    }
  }, [query]); // 의존성 배열 유지

  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest) =>
      mutatePurchaseOrders
        .delete(params)
        .mutationFn(params as ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          t("message.deleteSuccess", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: `${translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType)}${translateWithPartnerType(t, "purchaseOrder.id", partnerType)}`,
          }),
          t("common.deleteFail", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          "red",
        );
      },
    },
  );

  const columns: readonly Column<PurchaseOrdersGet200ResponseRowsInner>[] = useMemo(
    () => [
      {
        ...SelectColumn,
        resizable: true,
      },
      {
        key: "id",
        name: translateWithPartnerType(t, "purchaseOrder.id", partnerType),
        sortable: true,
        resizable: true,
      },
      {
        key: "code",
        name: translateWithPartnerType(t, "purchaseOrder.code", partnerType),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <PurchaseOrderDetailForm purchaseOrderId={formatterProps.row.id as number} />,
                  null,
                  "",
                )
              }
            >
              {formatterProps.row.code}
            </LinkAnchor>
          );
        },
      },
      {
        key: "name",
        name: translateWithPartnerType(t, "purchaseOrder.name", partnerType),
        sortable: true,
        resizable: true,
      },
      {
        key: "status",
        name: translateWithPartnerType(t, "purchaseOrder.status", partnerType),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Badge
              variant="filled"
              color={
                purchaseOrderStatusColor[
                  formatterProps.row.status as keyof typeof purchaseOrderStatusColor
                ]
              }
            >
              {setPurchaseOrderStatus(formatterProps.row.status ?? "")}
            </Badge>
          );
        },
      },
      {
        key: "partner.name",
        name: t("partner.name"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <PartnerDetailForm partnerCode={formatterProps.row.partner?.code} />,
                  null,
                  "",
                )
              }
            >
              {formatterProps.row.partner?.name}
            </LinkAnchor>
          );
        },
      },
      {
        key: "manager",
        name: t("order.manager"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps: any) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <UsersDetailForm UserCode={formatterProps.row?.userCode} />,
                  null,
                  t("common.detail", { item: t("user.user") }),
                )
              }
            >
              {formatterProps.row.manager}
            </LinkAnchor>
          );
        },
      },
      {
        key: "numberOfPurchaseOrderItems",
        name: translateWithPartnerType(t, "purchaseOrder.numberOfPurchaseOrderItems", partnerType),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Flex justify="flex-end">
              {setToLocaleString(formatterProps.row.numberOfPurchaseOrderItems)}
            </Flex>
          );
        },
      },
      {
        key: "sourceLocationName",
        name: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
        sortable: true,
        resizable: true,
        formatter: (formatterProps: any) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <LocationDetailForm locationCode={formatterProps.row.sourceLocationCode} />,
                  null,
                  t("common.detail", { item: t("location.location") }),
                )
              }
            >
              {formatterProps.row.sourceLocationName}
            </LinkAnchor>
          );
        },
      },
      {
        key: "orderDate",
        name: translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Flex align="center">
              {formatterProps.row.orderDate !== null
                ? timeUtil(formatterProps.row.orderDate as string)
                : ""}
            </Flex>
          );
        },
      },
      {
        key: "createdAt",
        name: t("order.createdAt"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Flex align="center">
              {formatterProps.row.createdAt !== null
                ? DatetimeUtil(formatterProps.row.createdAt as string)
                : ""}
            </Flex>
          );
        },
      },
      {
        key: "deliveryDeadline",
        name: t("order.deliveryDate"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          return (
            <Flex align="center">
              {formatterProps.row.deliveryDeadline !== null
                ? timeUtil(formatterProps.row.deliveryDeadline as string)
                : ""}
            </Flex>
          );
        },
      },

      {
        key: "options",
        name: t("common.action"),
        sortable: true,
        resizable: true,
        formatter: (formatterProps) => {
          const updatePurchaseOrderActionView = () => {
            openModal(
              <PurchaseOrderUpdateForm formatterProps={formatterProps?.row} />,
              null,
              t("common.edit", {
                item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
              }),
              true,
            );
          };

          const deletePurchaseOrderAction = () => {
            openModal(
              <ConfirmForm
                message={t("message.rowDelete")}
                yesCallback={() =>
                  deleteMutate({ purchaseOrderId: formatterProps?.row.id as number })
                }
                noCallback={() => {}}
                commonCallback={() => closeModal(null)}
              />,
              null,
              t("common.delete", {
                item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
              }),
            );
          };

          return (
            <Flex w="100%" h="100%" justify="center" align="center" gap="md">
              <ActionIcon variant="subtle" onClick={updatePurchaseOrderActionView} color="yellow">
                <IconEdit />
              </ActionIcon>
              <ActionIcon variant="subtle" onClick={() => deletePurchaseOrderAction()} color="red">
                <IconTrash />
              </ActionIcon>
            </Flex>
          );
        },
      },
    ],
    [deleteMutate, openModal, closeModal, t],
  );

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        t("message.deleteSelect", {
          item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
        }),
        t("common.deleteFail", {
          item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
        }),
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ purchaseOrderId: JSON.parse(row).id });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType) })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <Flex gap="sm">
          {partnerType === PartnersGet200ResponseRowsInnerTypeEnum.SALES ? null : (
            <Button
              leftIcon={<IconPlus />}
              onClick={() =>
                openModal(
                  <PurchaseOrderCreateForm />,
                  null,
                  t("common.additional", {
                    item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
                  }), // 수주 발주 추가
                  true,
                )
              }
            >
              {t("common.additional", {
                item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
              })}
            </Button>
          )}
          <Button
            leftIcon={<IconPlus />}
            color="orange"
            disabled={[...selectedRows].length !== 1} // 1개만 선택
            onClick={() =>
              openModal(
                <PurchaseOrderItemsOutgoingForm
                  formatterProps={JSON.parse([...selectedRows].at(0))}
                />,
                null,
                t("delivery.delivery"),
              )
            }
          >
            {t("delivery.action")}
          </Button>
        </Flex>
        <Flex gap="sm">
          <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
            {t("common.reset")}
          </Button>
          <Button rightIcon={<IconTrash />} color="red" onClick={() => handleDelete()}>
            {t("common.rowDelete")}
          </Button>
        </Flex>
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {"납품 정보 등록"}
          </Text>
          <Text fz="xs">
            {t("common.subTitle", {
              item: "납품 정보",
            })}
          </Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<PurchaseOrdersGet200Response, PurchaseOrdersGet200ResponseRowsInner>
            columns={columns}
            rowKeyGetter={(row: PurchaseOrdersGet200ResponseRowsInner) => JSON.stringify(row)}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<PurchaseOrdersGet200Response> />
      </Flex>
    </Flex>
  );
};
